/**
 * Reusable proxy-mode banner.
 *
 * `variant="banner"` (default): a warning-styled bar (44px tall, full-width,
 * only a 1px solid border-bottom). Designed to sit flush against the green
 * page header above it — the parent is responsible for placing it with zero
 * margin/gap.
 *
 * `variant="card"`: renders as a content card with rounded corners and margins
 * so it blends into the page body rather than spanning edge-to-edge. Use this
 * on pages where the banner sits below a scrollable content area (e.g. the
 * renter home page) instead of flush against the header.
 *
 * When `name` is null, undefined, or empty, the component renders nothing so
 * callers can use a single hook + banner combo without conditional wrapping.
 */
import { JSX } from 'react';
import { Box, Typography } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';

interface Props {
  name: string | null | undefined;
  /** Visual style. Defaults to "banner" (full-width bar flush to the header). */
  variant?: 'banner' | 'card';
}

export default function OnBehalfOfBanner({
  name,
  variant = 'banner',
}: Props): JSX.Element | null {
  if (!name) {
    return null;
  }

  if (variant === 'card') {
    return (
      <Box
        data-testid="on-behalf-of-banner"
        sx={{
          mx: 2,
          mt: 2,
          borderRadius: 2,
          bgcolor: 'warning.lighter',
          border: '1px solid',
          borderColor: 'warning.light',
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          gap: 1,
        }}
      >
        <PeopleIcon
          sx={{ fontSize: 18, color: 'warning.dark', flexShrink: 0 }}
        />
        <Typography
          sx={{ fontSize: 13, fontWeight: 600, color: 'warning.dark' }}
        >
          You are acting on behalf of {name}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid="on-behalf-of-banner"
      sx={{
        width: '100%',
        height: 44,
        bgcolor: 'warning.lighter',
        borderBottom: '1px solid',
        borderColor: 'warning.main',
        display: 'flex',
        alignItems: 'center',
        px: 2.5,
        gap: 1,
      }}
    >
      <PeopleIcon sx={{ fontSize: 16, color: 'warning.dark' }} />
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'warning.dark' }}>
        You are acting on behalf of {name}
      </Typography>
    </Box>
  );
}
